// Hybrid `<id>-<title-slug>` URL helpers (FR-009).
//
// Routing keys on the integer id; the slug tail is decorative and may
// drift if the upstream renames a hearing. The detail page parses the
// id, looks up the hearing, computes the canonical slug from the
// current title, and 308-redirects when the requested slug is stale.

const MAX_SLUG_TAIL_CHARS = 80;

export function buildSlug(id: number, title: string): string {
  const tail = slugifyTitle(title);
  return tail ? `${id}-${tail}` : String(id);
}

export type ParsedSlug = {
  id: number;
  tail: string;
};

export class InvalidSlugError extends Error {
  constructor(slug: string) {
    super(`invalid public-hearing slug: ${slug}`);
    this.name = "InvalidSlugError";
  }
}

// Splits `<id>` or `<id>-<tail>`. Throws when the leading segment
// isn't a positive integer; the route handler maps that to 404.
export function parseSlug(slug: string): ParsedSlug {
  const dash = slug.indexOf("-");
  const head = dash === -1 ? slug : slug.slice(0, dash);
  const tail = dash === -1 ? "" : slug.slice(dash + 1);
  if (!/^\d+$/.test(head)) throw new InvalidSlugError(slug);
  const id = Number.parseInt(head, 10);
  if (!Number.isFinite(id) || id <= 0) throw new InvalidSlugError(slug);
  return { id, tail };
}

function slugifyTitle(title: string): string {
  return title
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "") // strip combining diacritics
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, MAX_SLUG_TAIL_CHARS)
    .replace(/-+$/g, ""); // trim trailing dash that truncation may have left
}
